import {useCallback, useState} from 'react';
import {useSelector, useStore} from 'react-redux';
import {saveEventThunk} from '~/CreateFabricGreetingCard/api/event';
import {navigateToRoute, useCurrentRoute} from '~/CreateFabricGreetingCard/navigation';
import {ConfirmLeaveModalContainer} from '~/CreateFabricGreetingCard/navigation/ConfirmLeaveModal';
import {numSteps} from '~/CreateFabricGreetingCard/navigation/createSteps';
import {UPDATE_EVENT_DETAILS} from '~/CreateFabricGreetingCard/reducers/details/constants';
import {getEventId} from '~/CreateFabricGreetingCard/reducers/eviteEvent/selectors';
import {
  selectPristine,
  selectPristineUyo,
} from '~/CreateFabricGreetingCard/reducers/fabricDesign/selectors';
import {selectUsedPremiumFeature} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {isInAppMode} from '~/common/checkout/utils/app';
import {InjectableMegaNav} from '~/common/components/MegaNav/InjectableMegaNav';
import {shouldUseNamedStepsNav} from '~/common/utils/event_utils';
import {isFabricWebview} from '~/common/utils/webview';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {SimpleNav} from '~library/organisms/SimpleNav';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {DiamondIcon} from '~library/svg/DiamondIcon';
import {ProgressBar} from './ProgressBar';

import './NavHeader.sass';

export const NavHeader = ({isPremium}) => {
  const inAppMode = isInAppMode();
  const currentRoute = useCurrentRoute();
  const store = useStore();
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const showSteps = currentRoute.meta.countSteps;
  const user = useSelector((state) => state?.user);

  const pristine = useSelector(selectPristine);
  const pristineUyo = useSelector(selectPristineUyo);
  const usedPremiumFeature = useSelector(selectUsedPremiumFeature);
  const hasBeenSaved = !!useSelector(getEventId);

  const totalSteps =
    numSteps - useSelector((state) => (!state.details.isPremium && isFabricWebview() ? 1 : 0));
  const simpleNavObj = {
    prev: {
      onClick: async () => {
        store.dispatch({
          type: UPDATE_EVENT_DETAILS,
          payload: {preventUpdateModal: true},
        });
        await navigateToRoute('back', store);
      },
      text: 'Back',
      qaId: 'upsell-back',
      buttonVariant: 'secondary',
    },
    next: {
      onClick: async () => {
        store.dispatch({
          type: UPDATE_EVENT_DETAILS,
          payload: {preventUpdateModal: true},
        });
        await navigateToRoute('next', store);
      },
      text: 'Skip',
      qaId: 'upsell-next',
      buttonVariant: 'secondary',
    },
  };

  const onClickCloseBtn = useCallback(() => {
    if (pristine && pristineUyo) {
      return navigateToRoute('exit', store, true);
    }

    return setShowLeaveModal(true);
  }, [pristine, pristineUyo, store]);

  const onPreviewClick = useCallback(async () => {
    const savePromise = store.dispatch(saveEventThunk(true));
    await navigateToRoute('preview', store);
    await savePromise;
  }, [store]);

  const onBackClick = useCallback(() => navigateToRoute('back', store), [store]);

  const onLeaveClick = useCallback(async () => {
    setShowLeaveModal(false);
    await navigateToRoute('exit', store, !hasBeenSaved);
  }, [hasBeenSaved, store]);

  if (currentRoute.showMegaNav) {
    return <InjectableMegaNav user={user} />;
  }
  if (currentRoute.showSimpleNav) {
    return <SimpleNav prev={simpleNavObj.prev} next={simpleNavObj.next} />;
  }

  const useNamedStepsNav = shouldUseNamedStepsNav();
  const showPreviewNav = currentRoute.id === 1 && !isPremium && usedPremiumFeature;
  const showProgressBar =
    !showPreviewNav && (!useNamedStepsNav || (useNamedStepsNav && currentRoute.meta.editing));

  return (
    <>
      <div className="fabric_header__wrapper">
        <header className={`fabric_header${currentRoute.id > totalSteps ? ' frp' : ''}`}>
          <nav className={`fabric_header__nav${currentRoute.id > totalSteps ? ' frp' : ''}`}>
            {!inAppMode && (
              <Button
                id="continueBtn"
                data-qa-id="exit-create"
                className="fabric_header__back"
                variant="link"
                size="small"
                onClick={onClickCloseBtn}
                ariaLabel="Close"
              >
                <CloseLineIcon ratio={0.75} />
              </Button>
            )}
            <Typography variant="paragraph1" color="pebble" className="fabric-header__steps">
              {isPremium && <DiamondIcon ratio={16 / 14.0} className="fabric_header__is-premium" />}
              {isPremium && showSteps && ' '}
              {showSteps ? `${currentRoute.id} of ${totalSteps}:` : ''}
            </Typography>
            <Typography variant="paragraph1" className="fabric-header__step">
              &nbsp;
              {currentRoute.displayName || ''}
            </Typography>
            {currentRoute.linkPreview && (
              <Button
                id="continueBtn"
                data-qa-id="continueBtn"
                className="fabric_header__continue"
                variant="link"
                size="large"
                onClick={onPreviewClick}
                ariaLabel="Preview"
              >
                Preview
              </Button>
            )}
            {currentRoute.linkBack && (
              <Button
                id="continueBtn"
                data-qa-id="continueBtn"
                className="fabric_header__continue"
                variant="link"
                size="large"
                onClick={onBackClick}
                ariaLabel="Back"
              >
                Back
              </Button>
            )}
          </nav>
        </header>
        {showProgressBar && <ProgressBar />}
      </div>
      {showLeaveModal && (
        <ConfirmLeaveModalContainer
          isFirstStep={!hasBeenSaved}
          isEditingFlow={currentRoute.meta.editing}
          leave={onLeaveClick}
          cancelLeave={() => setShowLeaveModal(false)}
        />
      )}
    </>
  );
};
